import React, { useState, useEffect, useContext } from 'react';
import apiClient from '../utils/apiClient';
import { AuthContext } from '../context/AuthContext';

function UserPredictions() {
        const [predictions, setPredictions] = useState([]);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);
        const [drivers, setDrivers] = useState([]);
        const [teams, setTeams] = useState([]);
        const [raceResults, setRaceResults] = useState({});
        const auth = useContext(AuthContext);

        // First useEffect for fetching data
        useEffect(() => {
          const fetchData = async () => {
            try {
              setLoading(true);
              // Fetch predictions
              const predictionsRes = await apiClient.get('/api/predictions/user');
              
              // Fetch drivers and teams for display formatting
              const driversRes = await apiClient.get(`/api/f1data/drivers`);
              const teamsRes = await apiClient.get(`/api/f1data/teams`);
              
              setPredictions(predictionsRes.data);
              setDrivers(driversRes.data);
              setTeams(teamsRes.data);
              
              // Fetch race results for each prediction
              const raceResultsMap = {};
              for (const prediction of predictionsRes.data) {
                if (prediction.race?._id) {
                  try {
                    console.log(`Fetching results for race ${prediction.race._id}`);
                    const resultRes = await apiClient.get(`/api/results/race/${prediction.race._id}`);
                    console.log(`Results for race ${prediction.race._id}:`, resultRes.data);
                    raceResultsMap[prediction.race._id] = resultRes.data;
                  } catch (err) {
                    console.log(`No results yet for race ${prediction.race._id}:`, err.message);
                  }
                }
              }
              setRaceResults(raceResultsMap);
              
              setError(null);
            } catch (err) {
              console.error('Error fetching data:', err);
              setError('Failed to load your predictions');
            } finally {
              setLoading(false);
            }
          };

        fetchData();
      }, []);

        // Second useEffect for debugging
        useEffect(() => {
          if (predictions.length > 0) {
            console.log('First prediction:', predictions[0]);
            if (predictions[0].predictions) {
              console.log('Driver H2H:', predictions[0].predictions.driverHeadToHead);
              console.log('Team H2H:', predictions[0].predictions.teamHeadToHead);
            }
            console.log('Available drivers:', drivers.map(d => d.driverId));
            console.log('Available teams:', teams.map(t => t.constructorId));
          }
        }, [predictions, drivers, teams]);

        // Helper function to get driver display name
        const getDriverName = (driverId) => {
          if (!driverId) return 'Not selected';
          
          const driver = drivers.find(d => d.driverId === driverId);
          if (driver) {
            return `${driver.givenName} ${driver.familyName}`;
          }
          
          // If driver is not found in the drivers array, return the raw ID
          // with some basic formatting to make it more readable
          return driverId
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        };

        // Helper function to get team display name
        const getTeamName = (teamId) => {
          if (!teamId) return 'Not selected';
          
          const team = teams.find(t => t.constructorId === teamId);
          if (team) {
            return team.name;
          }
          
          // If team is not found in the teams array, return the raw ID
          // with some basic formatting to make it more readable
          return teamId
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        };

  if (loading) return <div className="text-center py-8">Loading your predictions...</div>;
  if (error) return <div className="bg-red-100 p-4 rounded">{error}</div>;
  if (!predictions.length) return <div className="text-center py-8">You haven't made any predictions yet.</div>;

  return (
    <div className="bg-black rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-4">Your Predictions</h2>
      <div className="space-y-6">
        {predictions.map((prediction) => (
          <div key={prediction._id} className="border p-4 rounded-lg bg-black-50">
            <h3 className="font-semibold text-lg mb-2">{prediction.race?.raceName || 'Unknown Race'}</h3>
            <p className="text-gray-500 text-sm mb-4">
              Submitted: {new Date(prediction.submitted || prediction.createdAt).toLocaleDateString()}
            </p>
            
            {/* Prediction Details */}
            {prediction.predictions && (
              <div className="space-y-4">
                {/* Podium */}
                <div className="bg-gray p-3 rounded shadow-sm">
                  <h4 className="font-medium text-gray-700 mb-2">Podium:</h4>
                  <div className="grid grid-cols-3 gap-4">
                    {/* P1 */}
                    <div className="text-center">
                      <div className="mb-2">
                        <div className="text-sm text-gray-600 mb-1">Your Prediction:</div>
                        <div className="font-medium text-white">{getDriverName(prediction.predictions.podium[0])}</div>
                      </div>
                      <div className="bg-yellow-100 text-black py-2 px-4 rounded-lg mb-2">P1</div>
                      <div className="text-sm text-gray-600 mb-1">Result:</div>
                      {(() => {
                        const raceResult = raceResults[prediction.race?._id];
                        return raceResult?.podium ? (
                          <div className="font-medium text-white">{getDriverName(raceResult.podium[0])}</div>
                        ) : (
                          <div className="text-gray-500 text-sm">No result yet</div>
                        );
                      })()}
                    </div>
                    
                    {/* P2 */}
                    <div className="text-center">
                      <div className="mb-2">
                        <div className="text-sm text-gray-600 mb-1">Your Prediction:</div>
                        <div className="font-medium text-white">{getDriverName(prediction.predictions.podium[1])}</div>
                      </div>
                      <div className="bg-gray-200 text-black py-2 px-4 rounded-lg mb-2">P2</div>
                      <div className="text-sm text-gray-600 mb-1">Result:</div>
                      {(() => {
                        const raceResult = raceResults[prediction.race?._id];
                        return raceResult?.podium ? (
                          <div className="font-medium text-white">{getDriverName(raceResult.podium[1])}</div>
                        ) : (
                          <div className="text-gray-500 text-sm">No result yet</div>
                        );
                      })()}
                    </div>
                    
                    {/* P3 */}
                    <div className="text-center">
                      <div className="mb-2">
                        <div className="text-sm text-gray-600 mb-1">Your Prediction:</div>
                        <div className="font-medium text-white">{getDriverName(prediction.predictions.podium[2])}</div>
                      </div>
                      <div className="bg-amber-600 text-white py-2 px-4 rounded-lg mb-2">P3</div>
                      <div className="text-sm text-gray-600 mb-1">Result:</div>
                      {(() => {
                        const raceResult = raceResults[prediction.race?._id];
                        return raceResult?.podium ? (
                          <div className="font-medium text-white">{getDriverName(raceResult.podium[2])}</div>
                        ) : (
                          <div className="text-gray-500 text-sm">No result yet</div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                    
                {/* Other predictions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Fastest Lap */}
                  <div className="bg-white p-3 rounded shadow-sm text-center">
                    <h4 className="font-medium text-gray-700 mb-1">Fastest Lap:</h4>
                    <div className="mb-2">
                      <div className="text-sm text-gray-600 mb-1">Your Prediction:</div>
                      <div className="text-gray-800 font-medium">{getDriverName(prediction.predictions.fastestLap)}</div>
                    </div>
                    <div className="border-t pt-2">
                      <div className="text-sm text-gray-600 mb-1">Actual Result:</div>
                      {(() => {
                        const raceResult = raceResults[prediction.race?._id];
                        return raceResult?.fastestLap ? (
                          <div className="text-gray-800 font-medium">{getDriverName(raceResult.fastestLap)}</div>
                        ) : (
                          <div className="text-gray-500 text-sm">No result yet</div>
                        );
                      })()}
                    </div>
                  </div>
                  
                  {/* Pole Position */}
                  <div className="bg-white p-3 rounded shadow-sm text-center">
                    <h4 className="font-medium text-gray-700 mb-1">Pole Position:</h4>
                    <div className="mb-2">
                      <div className="text-sm text-gray-600 mb-1">Your Prediction:</div>
                      <div className="text-gray-800 font-medium">{getDriverName(prediction.predictions.polePosition)}</div>
                    </div>
                    <div className="border-t pt-2">
                      <div className="text-sm text-gray-600 mb-1">Actual Result:</div>
                      {(() => {
                        const raceResult = raceResults[prediction.race?._id];
                        return raceResult?.polePosition ? (
                          <div className="text-gray-800 font-medium">{getDriverName(raceResult.polePosition)}</div>
                        ) : (
                          <div className="text-gray-500 text-sm">No result yet</div>
                        );
                      })()}
                    </div>
                  </div>
                  
                  {/* Last Place */}
                  <div className="bg-white p-3 rounded shadow-sm text-center">
                    <h4 className="font-medium text-gray-700 mb-1">Last Place:</h4>
                    <div className="mb-2">
                      <div className="text-sm text-gray-600 mb-1">Your Prediction:</div>
                      <div className="text-gray-800 font-medium">{getDriverName(prediction.predictions.firstRetirement)}</div>
                    </div>
                    <div className="border-t pt-2">
                      <div className="text-sm text-gray-600 mb-1">Actual Result:</div>
                      {(() => {
                        const raceResult = raceResults[prediction.race?._id];
                        return raceResult?.firstRetirement ? (
                          <div className="text-gray-800 font-medium">{getDriverName(raceResult.firstRetirement)}</div>
                        ) : (
                          <div className="text-gray-500 text-sm">No result yet</div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                
                {/* Head to Head predictions */}
                {/* Driver Head to Head section */}
                <div className="bg-white p-3 rounded shadow-sm">
                  <h4 className="font-medium text-gray-700 mb-1">Driver Head to Head:</h4>
                  {(() => {
                    const raceResult = raceResults[prediction.race?._id];
                    const hasHeadToHead = prediction.predictions?.driverHeadToHead?.winner && 
                                        raceResult?.headToHead?.drivers?.driver1?.name && 
                                        raceResult?.headToHead?.drivers?.driver2?.name;
                    
                    return hasHeadToHead ? (
                      <>
                        {/* Prediction options */}
                        <div className="mb-3">
                          <p className="text-sm text-gray-500 mb-1">Prediction options:</p>
                          <div className="flex justify-center items-center gap-8">
                            <span className="text-gray-700">{raceResult.headToHead.drivers.driver1.code || 'N/A'}</span>
                            <span className="mx-2 text-xs bg-blue-100 px-3 py-1 rounded">vs</span>
                            <span className="text-gray-700">{raceResult.headToHead.drivers.driver2.code || 'N/A'}</span>
                          </div>
                        </div>
                        
                        {/* Your prediction */}
                        <p className="text-sm text-gray-500 mb-1">
                          Your pick: <span className="font-semibold">{getDriverName(prediction.predictions.driverHeadToHead.winner)}</span>
                        </p>
                        
                        {/* Result */}
                        <div className="border-t pt-2">
                          <p className="text-sm text-gray-500 mb-1">Result:</p>
                          <p className="text-gray-800">
                            <strong>Winner:</strong> {raceResult.headToHead.drivers.winner ? 
                              getDriverName(raceResult.headToHead.drivers.winner) : 
                              'Not determined yet'}
                          </p>
                        </div>
                      </>
                    ) : (
                      <p className="text-gray-500">Not available for this race</p>
                    );
                  })()}
                </div>

                {/* Team Head to Head section */}
                <div className="bg-white p-3 rounded shadow-sm">
                  <h4 className="font-medium text-gray-700 mb-1">Team Head to Head:</h4>
                  {(() => {
                    const raceResult = raceResults[prediction.race?._id];
                    const hasTeamHeadToHead = prediction.predictions?.teamHeadToHead?.winner && 
                                            raceResult?.headToHead?.teams?.team1?.name && 
                                            raceResult?.headToHead?.teams?.team2?.name;
                    
                    return hasTeamHeadToHead ? (
                      <>
                        {/* Prediction options */}
                        <div className="mb-3">
                          <p className="text-sm text-gray-500 mb-1">Prediction options:</p>
                          <div className="flex justify-center items-center gap-8">
                            <span className="text-gray-700">{getTeamName(raceResult.headToHead.teams.team1.name) || 'N/A'}</span>
                            <span className="mx-2 text-xs bg-blue-100 px-3 py-1 rounded">vs</span>
                            <span className="text-gray-700">{getTeamName(raceResult.headToHead.teams.team2.name) || 'N/A'}</span>
                          </div>
                        </div>
                        
                        {/* Your prediction */}
                        <p className="text-sm text-gray-500 mb-1">
                          Your pick: <span className="font-semibold">{getTeamName(prediction.predictions.teamHeadToHead.winner)}</span>
                        </p>
                        
                        {/* Result */}
                        <div className="border-t pt-2">
                          <p className="text-sm text-gray-500 mb-1">Result:</p>
                          <p className="text-gray-800">
                            <strong>Winner:</strong> {raceResult.headToHead.teams.winner ? 
                              getTeamName(raceResult.headToHead.teams.winner) : 
                              'Not determined yet'}
                          </p>
                        </div>
                      </>
                    ) : (
                      <p className="text-gray-500">Not available for this race</p>
                    );
                  })()}
                </div>
              </div>
            )}
            
            {!prediction.predictions && (
              <div className="text-red-500 bg-white p-3 rounded shadow-sm">
                Prediction details missing or could not be loaded
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default UserPredictions;